import React, { useRef, useEffect } from 'react';
import { setupCanvas } from './canvasUtils';

// ============================================
// Chapter 8  -  Radioactivity
// ============================================

/** 8.2 – Alpha Decay */
export const AlphaDecayDiagram = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Alpha Decay  -  Heavy Nuclei Emit Helium-4', W / 2, 24);

        const cy = 140;

        // Parent nucleus (large)
        ctx.beginPath(); ctx.arc(120, cy, 50, 0, Math.PI * 2);
        ctx.fillStyle = '#DBEAFE'; ctx.fill();
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2; ctx.stroke();
        // Nucleons inside
        for (let i = 0; i < 12; i++) {
            const angle = (i / 12) * Math.PI * 2;
            const r = 15 + (i % 3) * 12;
            const x = 120 + r * Math.cos(angle);
            const y = cy + r * Math.sin(angle);
            ctx.beginPath(); ctx.arc(x, y, 5, 0, Math.PI * 2);
            ctx.fillStyle = i % 2 === 0 ? '#EF4444' : '#3B82F6'; ctx.fill();
        }
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('²³⁸U', 120, cy + 68);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Z = 92', 120, cy + 82);

        // Arrow
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(185, cy); ctx.lineTo(255, cy); ctx.stroke();
        ctx.fillStyle = '#94A3B8';
        ctx.beginPath(); ctx.moveTo(255, cy); ctx.lineTo(249, cy - 5); ctx.lineTo(249, cy + 5); ctx.closePath(); ctx.fill();

        // Daughter nucleus (slightly smaller)
        ctx.beginPath(); ctx.arc(330, cy, 42, 0, Math.PI * 2);
        ctx.fillStyle = '#DCFCE7'; ctx.fill();
        ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 2; ctx.stroke();
        for (let i = 0; i < 10; i++) {
            const angle = (i / 10) * Math.PI * 2;
            const r = 12 + (i % 3) * 10;
            const x = 330 + r * Math.cos(angle);
            const y = cy + r * Math.sin(angle);
            ctx.beginPath(); ctx.arc(x, y, 5, 0, Math.PI * 2);
            ctx.fillStyle = i % 2 === 0 ? '#EF4444' : '#3B82F6'; ctx.fill();
        }
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif';
        ctx.fillText('²³⁴Th', 330, cy + 60);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Z = 90', 330, cy + 74);

        // Plus sign
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 20px Inter,sans-serif';
        ctx.fillText('+', 400, cy + 5);

        // Alpha particle
        ctx.beginPath(); ctx.arc(460, cy, 22, 0, Math.PI * 2);
        ctx.fillStyle = '#FEF3C7'; ctx.fill();
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 2; ctx.stroke();
        // 2p + 2n inside
        const aPos = [[-7, -6], [7, -6], [-7, 6], [7, 6]];
        aPos.forEach(([dx, dy], i) => {
            ctx.beginPath(); ctx.arc(460 + dx, cy + dy, 5, 0, Math.PI * 2);
            ctx.fillStyle = i < 2 ? '#EF4444' : '#3B82F6'; ctx.fill();
        });
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif';
        ctx.fillText('⁴He (α)', 460, cy + 40);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Z = 2', 460, cy + 54);

        // Properties
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Charge: +2  |  Mass: 4 u  |  Range: few cm in air  |  Stopped by paper', W / 2, H - 28);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Alpha particles are the heaviest, least penetrating form of radiation', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 8.3 – Beta Decay (nucleus level) */
export const BetaDecayNucleus = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Beta Decay  -  A Neutron Becomes a Proton', W / 2, 24);

        // Left: Before
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Before', 120, 55);

        // Neutron
        ctx.beginPath(); ctx.arc(120, 120, 35, 0, Math.PI * 2);
        ctx.fillStyle = '#DBEAFE'; ctx.fill(); ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2; ctx.stroke();
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 14px Inter,sans-serif';
        ctx.fillText('n', 120, 125);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('(charge: 0)', 120, 168);

        // Arrow
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(175, 120); ctx.lineTo(245, 120); ctx.stroke();
        ctx.fillStyle = '#94A3B8';
        ctx.beginPath(); ctx.moveTo(245, 120); ctx.lineTo(238, 115); ctx.lineTo(238, 125); ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#475569'; ctx.font = 'italic 12px Inter,sans-serif';
        ctx.fillText('weak force', 210, 108);

        // Right: After (proton + electron + antineutrino)
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif';
        ctx.fillText('After', 380, 55);

        // Proton
        ctx.beginPath(); ctx.arc(320, 120, 30, 0, Math.PI * 2);
        ctx.fillStyle = '#FEE2E2'; ctx.fill(); ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2; ctx.stroke();
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 14px Inter,sans-serif';
        ctx.fillText('p', 320, 125);

        // Electron
        ctx.beginPath(); ctx.arc(400, 90, 16, 0, Math.PI * 2);
        ctx.fillStyle = '#DCFCE7'; ctx.fill(); ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 2; ctx.stroke();
        ctx.fillStyle = '#22C55E'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('e⁻', 400, 95);

        // Antineutrino
        ctx.beginPath(); ctx.arc(400, 155, 14, 0, Math.PI * 2);
        ctx.fillStyle = '#F3E8FF'; ctx.fill(); ctx.strokeStyle = '#A855F7'; ctx.lineWidth = 1.5; ctx.setLineDash([3, 2]); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#A855F7'; ctx.font = 'bold 11px Inter,sans-serif';
        ctx.fillText('ν\u0304ₑ', 400, 160);

        // Plus signs
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 16px Inter,sans-serif';
        ctx.fillText('+', 362, 95); ctx.fillText('+', 362, 148);

        // Equation
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('n → p + e⁻ + ν\u0304ₑ', W / 2, 210);

        // Quark level
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('At quark level: d → u + W⁻ → u + e⁻ + ν\u0304ₑ', W / 2, 235);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Beta decay changes element identity  -  key to radiocarbon dating', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-red-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 8.4 – Gamma Decay & EM Spectrum */
export const GammaDecaySpectrum = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Gamma Decay  -  Nucleus Emits High-Energy Photon', W / 2, 24);

        const cy = 100;
        // Excited nucleus
        ctx.beginPath(); ctx.arc(120, cy, 35, 0, Math.PI * 2);
        ctx.fillStyle = '#FEE2E2'; ctx.fill(); ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2; ctx.stroke();
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Excited', 120, cy - 5); ctx.fillText('nucleus', 120, cy + 10);
        // Squiggly lines indicating excitation
        ctx.strokeStyle = '#FBBF24'; ctx.lineWidth = 1.5;
        for (let i = 0; i < 3; i++) {
            const angle = -0.6 + i * 0.6;
            const x = 120 + 40 * Math.cos(angle);
            const y = cy + 40 * Math.sin(angle);
            ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + 8, y - 5); ctx.lineTo(x + 4, y + 5); ctx.stroke();
        }

        // Arrow
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(175, cy); ctx.lineTo(235, cy); ctx.stroke();
        ctx.fillStyle = '#94A3B8';
        ctx.beginPath(); ctx.moveTo(235, cy); ctx.lineTo(229, cy - 4); ctx.lineTo(229, cy + 4); ctx.closePath(); ctx.fill();

        // Ground state nucleus
        ctx.beginPath(); ctx.arc(300, cy, 35, 0, Math.PI * 2);
        ctx.fillStyle = '#DCFCE7'; ctx.fill(); ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 2; ctx.stroke();
        ctx.fillStyle = '#22C55E'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('Ground', 300, cy - 5); ctx.fillText('state', 300, cy + 10);

        // Gamma photon (wavy arrow)
        ctx.strokeStyle = '#8B5CF6'; ctx.lineWidth = 2.5;
        ctx.beginPath();
        for (let x = 260; x <= 420; x += 2) {
            const t = (x - 260) / 160;
            const y = 50 + Math.sin(t * 25) * 6;
            x === 260 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.fillStyle = '#8B5CF6'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('γ', 425, 55);

        // Energy levels (right side)
        const rx = 420, ry = 75;
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Energy Levels', rx + 55, ry);

        // Excited level
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(rx, ry + 20); ctx.lineTo(rx + 110, ry + 20); ctx.stroke();
        ctx.fillStyle = '#EF4444'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText('Excited', rx - 5, ry + 24);

        // Ground level
        ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(rx, ry + 80); ctx.lineTo(rx + 110, ry + 80); ctx.stroke();
        ctx.fillStyle = '#22C55E'; ctx.fillText('Ground', rx - 5, ry + 84);

        // Transition arrow
        ctx.strokeStyle = '#8B5CF6'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(rx + 55, ry + 25); ctx.lineTo(rx + 55, ry + 75); ctx.stroke();
        ctx.fillStyle = '#8B5CF6';
        ctx.beginPath(); ctx.moveTo(rx + 55, ry + 75); ctx.lineTo(rx + 51, ry + 68); ctx.lineTo(rx + 59, ry + 68); ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#8B5CF6'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('γ', rx + 62, ry + 55);

        // Properties
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Charge: 0  |  Mass: 0  |  Speed: c  |  Very penetrating (lead/concrete)', W / 2, 210);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Gamma rays are identical to photons but originate from nuclear transitions', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-purple-50 to-green-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 8.5 – Half-Life Curve */
export const HalfLifeCurve = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Radioactive Decay  -  Half-Life', W / 2, 24);

        const m = { top: 50, right: 30, bottom: 50, left: 60 };
        const pw = W - m.left - m.right, ph = H - m.top - m.bottom;

        // Axes
        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(m.left, m.top); ctx.lineTo(m.left, m.top + ph); ctx.lineTo(m.left + pw, m.top + ph); ctx.stroke();

        // Y label
        ctx.save(); ctx.translate(20, m.top + ph / 2); ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Remaining nuclei (%)', 0, 0); ctx.restore();

        // X label
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Number of half-lives', m.left + pw / 2, H - 6);

        // Y gridlines and labels
        [100, 50, 25, 12.5].forEach(pct => {
            const y = m.top + ph - (pct / 100) * ph;
            ctx.strokeStyle = '#F1F5F9'; ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(m.left, y); ctx.lineTo(m.left + pw, y); ctx.stroke();
            ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'right';
            ctx.fillText(pct + '%', m.left - 8, y + 4);
        });

        // X gridlines
        for (let i = 1; i <= 5; i++) {
            const x = m.left + (i / 5) * pw;
            ctx.strokeStyle = '#F1F5F9'; ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(x, m.top); ctx.lineTo(x, m.top + ph); ctx.stroke();
            ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(i.toString(), x, m.top + ph + 15);
        }

        // Decay curve N(t) = N₀ �- (1/2)^(t/t½)
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 3;
        ctx.beginPath();
        for (let px = 0; px <= pw; px++) {
            const halfLives = (px / pw) * 5;
            const fraction = Math.pow(0.5, halfLives);
            const x = m.left + px;
            const y = m.top + ph - fraction * ph;
            px === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Half-life markers (step-down annotations)
        const markers = [1, 2, 3];
        markers.forEach(hl => {
            const frac = Math.pow(0.5, hl);
            const x = m.left + (hl / 5) * pw;
            const y = m.top + ph - frac * ph;

            // Dashed lines to axes
            ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
            ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(m.left, y); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x, m.top + ph); ctx.stroke();
            ctx.setLineDash([]);

            // Dot
            ctx.beginPath(); ctx.arc(x, y, 4, 0, Math.PI * 2); ctx.fillStyle = '#EF4444'; ctx.fill();
        });

        // Formula
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('N(t) = N₀ �- (1/2)^(t/t½)', m.left + pw - 120, m.top + 20);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('After each half-life, exactly half the radioactive atoms remain', W / 2, H - 28);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-red-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 8.6 – Radiocarbon Dating (case study) */
export const RadiocarbonDating = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Radiocarbon Dating  -  How It Works', W / 2, 24);

        const steps = [
            { x: 60, label: 'Cosmic rays\ncreate ¹⁴C\nin atmosphere', color: '#3B82F6', icon: '☀' },
            { x: 170, label: '¹⁴C absorbed\nby living\norganisms', color: '#22C55E', icon: '🌿' },
            { x: 280, label: 'Organism dies\n¹⁴C starts\ndecaying', color: '#EF4444', icon: '⚰' },
            { x: 390, label: 'Measure ¹⁴C\nremaining\n→ age', color: '#8B5CF6', icon: '🔬' },
        ];

        steps.forEach((s, i) => {
            const y = 55;
            ctx.fillStyle = s.color + '10'; ctx.beginPath(); ctx.roundRect(s.x - 45, y, 100, 110, 10); ctx.fill();
            ctx.strokeStyle = s.color; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(s.x - 45, y, 100, 110, 10); ctx.stroke();

            ctx.fillStyle = s.color; ctx.font = '24px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(s.icon, s.x + 5, y + 30);

            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            s.label.split('\n').forEach((line, li) => {
                ctx.fillText(line, s.x + 5, y + 52 + li * 14);
            });

            // Arrow
            if (i < steps.length - 1) {
                ctx.fillStyle = '#94A3B8'; ctx.font = 'bold 18px Inter,sans-serif';
                ctx.fillText('→', s.x + 60, y + 55);
            }
        });

        // Key facts
        const fy = 185;
        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(30, fy, 500, 65, 10); ctx.fill();
        ctx.strokeStyle = '#BFDBFE'; ctx.lineWidth = 1; ctx.beginPath(); ctx.roundRect(30, fy, 500, 65, 10); ctx.stroke();

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('¹⁴C Half-life: 5,730 years', 50, fy + 20);
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('Effective range: up to ~50,000 years', 50, fy + 38);
        ctx.fillText('Decay: ¹⁴C → ¹⁴N + e⁻ + ν\u0304ₑ  (beta decay)', 50, fy + 54);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Willard Libby won the 1960 Nobel Prize for developing radiocarbon dating', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-green-50 to-purple-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 8.10 – Radioactivity Chapter Summary */
export const RadioactivitySummary = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Radiation Types Compared', W / 2, 24);

        const types = [
            { name: 'Alpha (α)', particle: '⁴He nucleus', charge: '+2', mass: '4 u', penetration: 'Paper', range: 'Few cm', color: '#EF4444' },
            { name: 'Beta (β)', particle: 'Electron', charge: '-1', mass: '~0', penetration: 'Aluminium', range: 'Metres', color: '#3B82F6' },
            { name: 'Gamma (γ)', particle: 'Photon', charge: '0', mass: '0', penetration: 'Lead/concrete', range: 'Very far', color: '#8B5CF6' },
        ];

        const headers = ['Type', 'Particle', 'Charge', 'Stopped by', 'Range in air'];
        const colX = [30, 120, 230, 300, 410];
        const colW = [90, 110, 70, 110, 110];

        // Header
        const hY = 48;
        ctx.fillStyle = '#1E293B'; ctx.beginPath(); ctx.roundRect(25, hY, W - 50, 28, [8, 8, 0, 0]); ctx.fill();
        headers.forEach((h, i) => {
            ctx.fillStyle = 'white'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(h, colX[i] + colW[i] / 2, hY + 19);
        });

        // Rows
        types.forEach((t, ri) => {
            const y = hY + 28 + ri * 45;
            ctx.fillStyle = ri % 2 === 0 ? '#F8FAFC' : 'white';
            ctx.fillRect(25, y, W - 50, 45);
            ctx.fillStyle = t.color; ctx.fillRect(25, y, 4, 45);

            const cells = [t.name, t.particle, t.charge, t.penetration, t.range];
            cells.forEach((cell, ci) => {
                ctx.fillStyle = ci === 0 ? t.color : '#475569';
                ctx.font = ci === 0 ? 'bold 12px Inter,sans-serif' : '12px Inter,sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText(cell, colX[ci] + colW[ci] / 2, y + 28);
            });
        });

        // Shielding illustration
        const sy = hY + 28 + 3 * 45 + 15;
        ctx.fillStyle = '#475569'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Shielding:', 30, sy);

        // Paper
        ctx.fillStyle = '#FEF3C7'; ctx.fillRect(140, sy - 18, 15, 28);
        ctx.strokeStyle = '#D97706'; ctx.lineWidth = 1; ctx.strokeRect(140, sy - 18, 15, 28);
        ctx.fillStyle = '#D97706'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Paper', 148, sy + 22);
        // α stopped
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText('α �-', 135, sy + 2);

        // Aluminium
        ctx.fillStyle = '#E2E8F0'; ctx.fillRect(250, sy - 18, 20, 28);
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 1; ctx.strokeRect(250, sy - 18, 20, 28);
        ctx.fillStyle = '#64748B'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Aluminium', 260, sy + 22);
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText('β �-', 245, sy + 2);

        // Lead
        ctx.fillStyle = '#94A3B8'; ctx.fillRect(380, sy - 18, 30, 28);
        ctx.strokeStyle = '#475569'; ctx.lineWidth = 1; ctx.strokeRect(380, sy - 18, 30, 28);
        ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Lead', 395, sy + 22);
        ctx.fillStyle = '#8B5CF6'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText('γ �-', 375, sy + 2);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-red-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


// ============================================
// Chapter 9  -  A History of CERN
// ============================================

/** 9.1 – CERN Founding */
export const CERNFounding = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The Founding of CERN  -  Science for Peace', W / 2, 24);

        const cards = [
            { title: 'Founded', value: '1954', desc: '12 founding\nmember states', color: '#3B82F6' },
            { title: 'Location', value: 'Geneva', desc: 'Franco-Swiss\nborder', color: '#22C55E' },
            { title: 'Mission', value: 'Peace', desc: 'Fundamental\nresearch', color: '#F59E0B' },
            { title: 'Today', value: '23', desc: 'Member\nstates', color: '#8B5CF6' },
        ];

        cards.forEach((card, i) => {
            const x = 20 + i * 135, y = 50, w = 122, h = 145;
            ctx.fillStyle = card.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 12); ctx.fill();
            ctx.strokeStyle = card.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(x, y, w, h, 12); ctx.stroke();

            ctx.fillStyle = card.color; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(card.title, x + w / 2, y + 22);
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 24px Inter,sans-serif';
            ctx.fillText(card.value, x + w / 2, y + 60);
            ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
            card.desc.split('\n').forEach((line, li) => {
                ctx.fillText(line, x + w / 2, y + 85 + li * 14);
            });
        });

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('CERN = Conseil Europeen pour la Recherche Nucleaire', W / 2, 215);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Born from the vision that science transcends borders  -  now the world\'s largest particle physics lab', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 9.2 – PS and Early Discoveries */
export const PSEarlyDiscoveries = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The Proton Synchrotron & Early Discoveries', W / 2, 24);

        const events = [
            { year: '1959', label: 'PS starts\n(28 GeV)', color: '#3B82F6' },
            { year: '1965', label: 'Antideuteron\ndiscovered', color: '#22C55E' },
            { year: '1964', label: 'CP violation\n(Brookhaven)', color: '#F59E0B' },
            { year: '1973', label: 'Neutral currents\n(Gargamelle)', color: '#EF4444' },
            { year: '1981', label: 'pp\u0304 collisions\nbegin (Spp\u0304S)', color: '#8B5CF6' },
        ];

        const axY = 130, axL = 50, axR = 510;
        ctx.strokeStyle = '#CBD5E1'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(axL, axY); ctx.lineTo(axR, axY); ctx.stroke();

        const gap = (axR - axL) / (events.length - 1);
        events.forEach((ev, i) => {
            const x = axL + i * gap;
            const above = i % 2 === 0;

            ctx.beginPath(); ctx.arc(x, axY, 7, 0, Math.PI * 2);
            ctx.fillStyle = ev.color; ctx.fill();

            ctx.strokeStyle = ev.color; ctx.lineWidth = 1.5;
            const cardY = above ? axY - 80 : axY + 15;
            ctx.beginPath(); ctx.moveTo(x, axY + (above ? -7 : 7)); ctx.lineTo(x, cardY + (above ? 65 : 0)); ctx.stroke();

            ctx.fillStyle = ev.color; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(ev.year, x, cardY + (above ? 12 : 18));
            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            ev.label.split('\n').forEach((line, li) => {
                ctx.fillText(line, x, cardY + (above ? 28 : 34) + li * 14);
            });
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The PS is still operating today as part of the LHC injection chain', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 9.3 – W and Z Boson Discovery (case study) */
export const WZDiscovery = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Discovery of W and Z Bosons (1983)', W / 2, 24);

        // W boson card
        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(30, 50, 220, 150, 12); ctx.fill();
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(30, 50, 220, 150, 12); ctx.stroke();
        ctx.fillStyle = '#2563EB'; ctx.font = 'bold 18px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('W± Boson', 140, 80);
        ctx.fillStyle = '#475569'; ctx.font = '13px Inter,sans-serif';
        ctx.fillText('Mass: 80.4 GeV/c²', 140, 105);
        ctx.fillText('Charge: ±1', 140, 125);
        ctx.fillText('Carries: Weak force', 140, 145);
        ctx.fillText('Enables: Beta decay', 140, 165);
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('January 1983  -  UA1', 140, 188);

        // Z boson card
        ctx.fillStyle = '#FEF3C7'; ctx.beginPath(); ctx.roundRect(290, 50, 220, 150, 12); ctx.fill();
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(290, 50, 220, 150, 12); ctx.stroke();
        ctx.fillStyle = '#D97706'; ctx.font = 'bold 18px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Z⁰ Boson', 400, 80);
        ctx.fillStyle = '#475569'; ctx.font = '13px Inter,sans-serif';
        ctx.fillText('Mass: 91.2 GeV/c²', 400, 105);
        ctx.fillText('Charge: 0', 400, 125);
        ctx.fillText('Carries: Weak force', 400, 145);
        ctx.fillText('Enables: Neutral currents', 400, 165);
        ctx.fillStyle = '#F59E0B'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('June 1983  -  UA1 & UA2', 400, 188);

        // Nobel Prize
        ctx.fillStyle = '#FEF2F2'; ctx.beginPath(); ctx.roundRect(120, 215, 300, 35, 8); ctx.fill();
        ctx.strokeStyle = '#FECACA'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(120, 215, 300, 35, 8); ctx.stroke();
        ctx.fillStyle = '#DC2626'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('🏆 1984 Nobel Prize  -  Rubbia & van der Meer', 270, 238);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Confirmed electroweak unification  -  the weak and EM forces are one', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 9.4 – The World Wide Web */
export const WorldWideWebTimeline = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 240;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The World Wide Web  -  Born at CERN', W / 2, 24);

        const events = [
            { year: '1989', label: 'Tim Berners-Lee\nproposes WWW', color: '#3B82F6' },
            { year: '1990', label: 'First web server\n& browser', color: '#22C55E' },
            { year: '1991', label: 'First external\nweb site', color: '#F59E0B' },
            { year: '1993', label: 'CERN makes\nWWW free', color: '#EF4444' },
        ];

        const axY = 100, axL = 80, axR = 480;
        ctx.strokeStyle = '#CBD5E1'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(axL, axY); ctx.lineTo(axR, axY); ctx.stroke();

        const gap = (axR - axL) / (events.length - 1);
        events.forEach((ev, i) => {
            const x = axL + i * gap;
            ctx.beginPath(); ctx.arc(x, axY, 8, 0, Math.PI * 2);
            ctx.fillStyle = ev.color; ctx.fill();

            ctx.fillStyle = ev.color; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(ev.year, x, axY - 18);

            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            ev.label.split('\n').forEach((line, li) => {
                ctx.fillText(line, x, axY + 28 + li * 14);
            });
        });

        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(80, 165, 400, 40, 8); ctx.fill();
        ctx.strokeStyle = '#BFDBFE'; ctx.lineWidth = 1; ctx.beginPath(); ctx.roundRect(80, 165, 400, 40, 8); ctx.stroke();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Originally created to share physics data between CERN researchers', W / 2, 183);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Now used by 5+ billion people worldwide', W / 2, 198);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('CERN\'s greatest gift to society  -  open, free, and universal', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={240} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 9.5+9.6 – LEP to LHC */
export const LEPToLHC = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('From LEP to LHC  -  The Same Tunnel', W / 2, 24);

        // LEP card (left)
        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(30, 50, 230, 170, 12); ctx.fill();
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(30, 50, 230, 170, 12); ctx.stroke();
        ctx.fillStyle = '#2563EB'; ctx.font = 'bold 16px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('LEP (1989-2000)', 145, 78);

        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        const lepFacts = ['Electron-Positron collider', 'Energy: 209 GeV (total)', 'Circumference: 26.7 km', 'Precisely measured W, Z', 'Ruled out light Higgs < 114 GeV'];
        lepFacts.forEach((f, i) => ctx.fillText(f, 145, 100 + i * 20));

        // Arrow
        ctx.fillStyle = '#94A3B8'; ctx.font = 'bold 28px Inter,sans-serif';
        ctx.fillText('→', 270, 135);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Same', 280, 160); ctx.fillText('tunnel', 280, 174);

        // LHC card (right)
        ctx.fillStyle = '#F0FDF4'; ctx.beginPath(); ctx.roundRect(300, 50, 230, 170, 12); ctx.fill();
        ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(300, 50, 230, 170, 12); ctx.stroke();
        ctx.fillStyle = '#16A34A'; ctx.font = 'bold 16px Inter,sans-serif';
        ctx.fillText('LHC (2008-present)', 415, 78);

        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        const lhcFacts = ['Proton-Proton collider', 'Energy: 13.6 TeV (total)', 'Same tunnel as LEP!', 'Discovered the Higgs (2012)', '~600 million collisions/sec'];
        lhcFacts.forEach((f, i) => ctx.fillText(f, 415, 100 + i * 20));

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('LEP dismantled to make room for LHC  -  a proton collider with 65�- more energy', W / 2, H - 28);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('The 26.7 km tunnel 100m underground remains one of the largest structures ever built', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 9.10 – CERN History Chapter Summary */
export const CERNHistorySummary = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('CERN\'s Greatest Hits', W / 2, 24);

        const achievements = [
            { year: '1954', text: 'Founded', color: '#3B82F6' },
            { year: '1973', text: 'Neutral currents', color: '#22C55E' },
            { year: '1983', text: 'W & Z bosons', color: '#F59E0B' },
            { year: '1989', text: 'World Wide Web', color: '#EF4444' },
            { year: '1995', text: 'Antihydrogen', color: '#8B5CF6' },
            { year: '2012', text: 'Higgs boson', color: '#EC4899' },
        ];

        const startX = 45, gap = 85;
        achievements.forEach((a, i) => {
            const x = startX + i * gap, y = 55;
            ctx.fillStyle = a.color + '15'; ctx.beginPath(); ctx.roundRect(x - 35, y, 72, 105, 10); ctx.fill();
            ctx.strokeStyle = a.color; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(x - 35, y, 72, 105, 10); ctx.stroke();

            ctx.fillStyle = a.color; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(a.year, x + 1, y + 25);
            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            const words = a.text.split(' ');
            words.forEach((w, wi) => ctx.fillText(w, x + 1, y + 50 + wi * 14));
        });

        // Bottom
        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(30, 178, 500, 45, 8); ctx.fill();
        ctx.strokeStyle = '#BFDBFE'; ctx.lineWidth = 1; ctx.beginPath(); ctx.roundRect(30, 178, 500, 45, 8); ctx.stroke();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('7 Nobel Prizes  •  23 Member States  •  17,000+ scientists from 110+ countries', W / 2, 198);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('The world\'s largest and most successful particle physics laboratory', W / 2, 216);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('From 12 nations and one accelerator to the frontier of human knowledge', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-pink-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};
